import bookingCycleMgr from "@services/bookingCycleMgr";
import subscriptions from "@services/subscriptions/instance";
import axios from "axios";
import { formatSessionIdAsGuid } from "../helpers/guidFormatter";
import type { Five9WebSocketTester } from "../types";

/**
 * Load a single script
 */
const loadScript = (src: string): Promise<void> => {
	return new Promise((resolve, reject) => {
		// Check if already loaded
		if (document.querySelector(`script[src="${src}"]`)) {
			resolve();
			return;
		}

		const script = document.createElement("script");
		script.src = src;
		script.async = false; // Load in order

		script.onload = () => {
			console.log(`✅ [FIVE9_STATE] Loaded: ${src}`);
			resolve();
		};

		script.onerror = () => {
			console.error(`❌ [FIVE9_STATE] Failed to load: ${src}`);
			reject(new Error(`Failed to load script: ${src}`));
		};

		document.head.appendChild(script);
	});
};

/**
 * Load Five9 scripts dynamically from public directory
 */
export const loadFive9Scripts = async (): Promise<boolean> => {
	try {
		console.log("🔄 [FIVE9_STATE] Loading Five9 WebSocket scripts...");

		// Check if already loaded
		if (window.five9Tester) {
			console.log("✅ [FIVE9_STATE] Five9 already loaded");
			return true;
		}

		// Make axios globally available for Five9 scripts
		if (!window.axios) {
			window.axios = axios;
			console.log("✅ [FIVE9_STATE] Made axios globally available");
		}

		// List of scripts to load in order - handlers must come before factory
		const scriptsToLoad = [
			"/five9-websocket-tester/five9-auth.js",
			"/five9-websocket-tester/state-parser.js",
			"/five9-websocket-tester/event-collector.js",
			"/five9-websocket-tester/handlers/IEventHandler.js",
			"/five9-websocket-tester/handlers/AgentPresenceHandler.js",
			"/five9-websocket-tester/handlers/CallEventHandler.js",
			"/five9-websocket-tester/handlers/InteractionEventHandler.js",
			"/five9-websocket-tester/handlers/MessagingEventHandler.js",
			"/five9-websocket-tester/handlers/QueueEventHandler.js",
			"/five9-websocket-tester/handlers/RecordingEventHandler.js",
			"/five9-websocket-tester/handlers/SkillStatisticsHandler.js",
			"/five9-websocket-tester/handlers/StationEventHandler.js",
			"/five9-websocket-tester/handlers/SystemEventHandler.js",
			"/five9-websocket-tester/managers/EventHandlerFactory.js",
			"/five9-websocket-tester/websocket-tester.js",
		];

		// Load each script sequentially
		for (const scriptPath of scriptsToLoad) {
			await loadScript(scriptPath);
		}

		// Wait for initialization
		await new Promise((resolve) => setTimeout(resolve, 500));

		// Check if instance already exists (created by DOMContentLoaded in websocket-tester.js)
		if (window.five9Tester) {
			console.log(
				"✅ [FIVE9_STATE] Five9WebSocketTester instance found (created by script)",
			);
			return true;
		}

		// Manually create instance since DOMContentLoaded may have already fired
		if (window.Five9WebSocketTester) {
			console.log(
				"🔄 [FIVE9_STATE] Creating Five9WebSocketTester instance manually...",
			);
			window.five9Tester = new window.Five9WebSocketTester();
			console.log(
				"✅ [FIVE9_STATE] Five9WebSocketTester instance created manually",
			);
			return true;
		}

		// Check global scope for the class (it might be attached differently)
		const globalWindow = window as typeof window & {
			Five9WebSocketTester?: new () => Five9WebSocketTester;
		};
		if (typeof globalWindow.Five9WebSocketTester !== "undefined") {
			console.log(
				"🔄 [FIVE9_STATE] Found Five9WebSocketTester in global scope...",
			);
			window.five9Tester = new globalWindow.Five9WebSocketTester();
			console.log(
				"✅ [FIVE9_STATE] Five9WebSocketTester instance created from global",
			);
			return true;
		}

		console.warn(
			"⚠️ [FIVE9_STATE] Five9WebSocketTester class not found after loading scripts",
		);
		console.log(
			"Available globals:",
			Object.keys(window).filter((key) => key.includes("Five9")),
		);
		return false;
	} catch (error) {
		console.error("❌ [FIVE9_STATE] Failed to load Five9 scripts:", error);
		return false;
	}
};

/**
 * Set up Five9 callbacks for incoming calls and agent state changes
 */
export const setupFive9Callbacks = (five9Tester: Five9WebSocketTester) => {
	// Set up the callback for incoming calls
	five9Tester.setIncomingCallCallback(
		async (
			callerNumber: string,
			dialedNumber: string,
			callSessionId: string,
			callId?: string | null,
			five9AccountCode?: string | null,
			five9QueueKey?: string | null,
		) => {
			console.log(
				`📞 [FIVE9_STATE] Incoming call: ${callerNumber} → ${dialedNumber} [${callSessionId}] CallID: ${callId || "N/A"} Account: ${five9AccountCode || "N/A"} Queue: ${five9QueueKey || "N/A"}`,
			);

			// Check for duplicate session
			if (bookingCycleMgr.hasSessionId(callSessionId)) {
				console.warn(
					`⚠️ [FIVE9_STATE] Session ${callSessionId} already exists - ignoring callback`,
				);
				return;
			}

			// Publish call data to subscription system
			subscriptions.publish("CallFrom", callerNumber);
			subscriptions.publish("CallTo", dialedNumber);
			subscriptions.publish("SessionId", callSessionId);
			subscriptions.publish("AgentState", "Connected");
			subscriptions.publish("IsOutgoingCall", false);
			// Five9 doesn't provide real database eventIds like other telephony systems
			// Since we're using direct routing, no database event exists yet - use null
			subscriptions.publish("EventId", null);
			// Publish call ID if available from WebSocket, otherwise null
			subscriptions.publish("CallId", callId);
			console.log(
				`📝 [FIVE9_STATE] No database event exists for Five9 call - eventId set to null`,
			);

			// Format session ID as GUID if needed (add dashes in correct places)
			const formattedSessionId = formatSessionIdAsGuid(callSessionId);
			console.log(
				`🔍 [FIVE9_STATE] Formatted session ID: ${callSessionId} → ${formattedSessionId}`,
			);

			// DIRECT APPROACH: Use Five9 extracted account code when available
			let finalAccountCode: number;

			if (
				five9AccountCode &&
				five9AccountCode !== "0" &&
				five9AccountCode.trim() !== ""
			) {
				// Use Five9 extracted account code directly
				finalAccountCode = Number(five9AccountCode);
				console.log(
					`🚀 [FIVE9_STATE] Using Five9 extracted account code directly: ${finalAccountCode}`,
				);
			} else {
				// Fallback to default account code 44999 when Five9 data is missing or invalid
				finalAccountCode = 44999;
				console.log(
					`⚠️ [FIVE9_STATE] Five9 account code missing/invalid (${five9AccountCode}), using default: ${finalAccountCode}`,
				);
			}

			// Publish the account code immediately (no API delay)
			subscriptions.publish("AccountCode", finalAccountCode);

			// Optional: Publish Five9 queue key if available
			if (five9QueueKey) {
				console.log(
					`🔍 [FIVE9_STATE] Five9 queue key available: ${five9QueueKey}`,
				);
				// Could publish queue key to subscriptions if needed for routing logic
			}

			console.log(
				`✅ [FIVE9_STATE] Successfully processed incoming call for session: ${callSessionId}`,
			);
		},
	);

	// Set up the callback for agent state changes
	five9Tester.setAgentStateCallback((data) => {
		console.log(`👤 [FIVE9_STATE] Agent state update:`, data);

		// Parse agent state to extract presence and reason
		const agentStatus = data.agentState || "UNKNOWN";
		let agentState = "Offline";

		// Map Five9 agent states to our state system
		if (agentStatus.includes("READY")) {
			agentState = "Ready";
		} else if (agentStatus.includes("NOT_READY")) {
			agentState = "NotReady";
		} else if (
			agentStatus.includes("WORKING") ||
			agentStatus.includes("ON_CALL")
		) {
			agentState = "Connected";
		} else if (agentStatus.includes("WRAP")) {
			agentState = "WrapUp";
		}

		// Publish agent state updates to subscription system
		subscriptions.publish("AgentState", agentState);

		// Handle call cleanup when agent is no longer connected to a call
		if (agentState !== "Connected") {
			console.log(
				`📞 [FIVE9_STATE] Call ended - agent state changed to: ${agentState}, clearing call data`,
			);

			// Clear all call-related data to prevent booking recreation
			subscriptions.publish("CallFrom", null);
			subscriptions.publish("CallTo", null);
			subscriptions.publish("SessionId", null);
			subscriptions.publish("EventId", null);
			subscriptions.publish("AccountCode", null);
			subscriptions.publish("IsOutgoingCall", false);
			subscriptions.publish("CallId", null);

			// Clear booking cycle manager state
			bookingCycleMgr.setCurrentBookingSessionId("");
			bookingCycleMgr.setCurrentBookingEventId(0);
			bookingCycleMgr.setCurrentBookingCallId("");

			console.log(
				"✅ [FIVE9_STATE] Call data cleared - no more booking creation attempts",
			);
		} else {
			// If there's call session information, publish it too (only when connected)
			if (data.callSessionId) {
				subscriptions.publish("SessionId", data.callSessionId);
			}
		}

		console.log(
			`✅ [FIVE9_STATE] Published agent state: ${agentState} (from ${agentStatus})`,
		);
	});
};

/**
 * Get current Five9 state and publish it immediately
 */
export const publishCurrentState = (five9Tester: Five9WebSocketTester) => {
	// Get current state immediately if Five9 is connected and has state
	if (five9Tester.stateParser) {
		const currentStatusData = five9Tester.stateParser.getStatusBarData();
		console.log(
			"📊 [FIVE9_STATE] Getting current Five9 state:",
			currentStatusData,
		);

		if (currentStatusData?.agentStatus) {
			const agentStatus = currentStatusData.agentStatus;
			let agentState = "Offline";

			// Map Five9 agent states to our state system
			if (agentStatus.includes("READY")) {
				agentState = "Ready";
			} else if (agentStatus.includes("NOT_READY")) {
				agentState = "NotReady";
			} else if (
				agentStatus.includes("WORKING") ||
				agentStatus.includes("ON_CALL")
			) {
				agentState = "Connected";
			} else if (agentStatus.includes("WRAP")) {
				agentState = "WrapUp";
			}

			// Immediately publish current state
			subscriptions.publish("AgentState", agentState);
			console.log(
				`✅ [FIVE9_STATE] Published initial agent state: ${agentState} (from ${agentStatus})`,
			);

			// If there's a current call session, publish it
			if (currentStatusData.callSessionId) {
				subscriptions.publish("SessionId", currentStatusData.callSessionId);
			}
		}
	}
};

/**
 * Initialize Five9 WebSocket integration
 */
export const initializeFive9Integration = async (
	rightioUserId: number,
): Promise<boolean> => {
	console.log("🔄 [FIVE9_STATE] Initializing Five9 WebSocket integration...");

	try {
		// First, load the Five9 scripts
		const scriptsLoaded = await loadFive9Scripts();
		if (!scriptsLoaded) {
			throw new Error("Failed to load Five9 scripts");
		}

		// Wait for the instance to be ready
		const maxWaitTime = 3000; // 3 seconds
		const checkInterval = 100; // 100ms
		let waited = 0;

		while (!window.five9Tester && waited < maxWaitTime) {
			console.log(
				"🔄 [FIVE9_STATE] Waiting for Five9WebSocketTester to initialize...",
			);
			await new Promise((resolve) => setTimeout(resolve, checkInterval));
			waited += checkInterval;
		}

		if (!window.five9Tester) {
			throw new Error(
				"Five9WebSocketTester not available after loading scripts",
			);
		}

		console.log("✅ [FIVE9_STATE] Found Five9WebSocketTester instance");

		// Set user-specific prefix for localStorage and trigger auto-login
		if (rightioUserId && rightioUserId > 0 && window.five9Tester.authService) {
			window.five9Tester.authService.setUserPrefix(
				`rightio_user_${rightioUserId}`,
			);
			console.log(
				`🔐 [FIVE9_STATE] Set user prefix: rightio_user_${rightioUserId}`,
			);

			// Try auto-login after user prefix is set
			const autoLoginSuccess =
				await window.five9Tester.authService.tryAutoLogin();
			if (autoLoginSuccess) {
				console.log("✅ [FIVE9_STATE] Auto-login successful");

				// Start WebSocket connection after successful auto-login
				if (!window.five9Tester.isConnected) {
					console.log(
						"🔄 [FIVE9_STATE] Starting WebSocket connection after auto-login...",
					);
					try {
						await window.five9Tester.connect();
						console.log("✅ [FIVE9_STATE] WebSocket connection established");
					} catch (error) {
						console.error(
							"❌ [FIVE9_STATE] Failed to connect WebSocket:",
							error,
						);
					}
				} else {
					console.log("✅ [FIVE9_STATE] WebSocket already connected");
				}
			} else {
				console.log(
					"❌ [FIVE9_STATE] Auto-login failed - user will need to login manually",
				);
			}
		}

		// Publish current state immediately
		publishCurrentState(window.five9Tester);

		// Set up callbacks
		setupFive9Callbacks(window.five9Tester);

		console.log("✅ [FIVE9_STATE] Five9 callbacks registered successfully");
		return true;
	} catch (error) {
		console.error(
			"❌ [FIVE9_STATE] Failed to initialize Five9 integration:",
			error,
		);
		return false;
	}
};

/**
 * Clean up Five9 integration
 */
export const cleanupFive9Integration = () => {
	console.log(
		"🔄 [FIVE9_STATE] Component unmounting, cleaning up subscriptions and Five9 callback...",
	);

	// Remove Five9 callbacks
	if (window.five9Tester) {
		window.five9Tester.removeIncomingCallCallback();
		window.five9Tester.removeAgentStateCallback();
		console.log("🔄 [FIVE9_STATE] Five9 callbacks removed");

		// Clear session and localStorage to prevent auto-login on next initialization
		if (window.five9Tester.authService) {
			console.log(
				"🔄 [FIVE9_STATE] About to clear Five9 session and localStorage",
			);
			console.log(
				"🔍 [FIVE9_STATE] Current userPrefix:",
				window.five9Tester.authService.userPrefix,
			);

			// Check what's in localStorage before clearing
			const sessionKey =
				window.five9Tester.authService.getStorageKey("five9_session");
			const credentialsKey =
				window.five9Tester.authService.getStorageKey("five9_credentials");
			console.log(
				"🔍 [FIVE9_STATE] About to clear keys:",
				sessionKey,
				credentialsKey,
			);
			console.log(
				"🔍 [FIVE9_STATE] Session exists before clear:",
				localStorage.getItem(sessionKey) !== null,
			);
			console.log(
				"🔍 [FIVE9_STATE] Credentials exists before clear:",
				localStorage.getItem(credentialsKey) !== null,
			);

			window.five9Tester.authService.clearSession();

			// Verify they were actually cleared
			console.log(
				"🔍 [FIVE9_STATE] Session exists after clear:",
				localStorage.getItem(sessionKey) !== null,
			);
			console.log(
				"🔍 [FIVE9_STATE] Credentials exists after clear:",
				localStorage.getItem(credentialsKey) !== null,
			);
			console.log("✅ [FIVE9_STATE] Five9 session and localStorage cleared");
		}
	}
};
